"use client"

import { useEffect, useState, type ReactNode } from "react"
import { useAuth } from "@/contexts/auth-context"
import { hasRequiredRoles } from "@/lib/jwt-utils"
import { Loader2, AlertCircle } from "lucide-react"
import { Card } from "@/components/ui/card"

interface RoleGuardProps {
  children: ReactNode
  requiredRoles: string[]
  unauthorizedMessage?: string
}

/**
 * RoleGuard component that blocks access if user doesn't have required roles
 */
export function RoleGuard({ 
  children, 
  requiredRoles,
  unauthorizedMessage = "You do not have permission to access this application. Please contact your administrator."
}: RoleGuardProps) {
  const { token, isAuthenticated } = useAuth()
  const [hasAccess, setHasAccess] = useState<boolean | null>(isAuthenticated ? null : false)

  useEffect(() => {
    if (!isAuthenticated || !token) {
      setHasAccess(false)
      return
    }

    // Check if user has required roles
    const userHasAccess = hasRequiredRoles(token, requiredRoles)
    setHasAccess(userHasAccess)
  }, [token, isAuthenticated, requiredRoles])

  // Show loading while checking authentication
  if (!isAuthenticated) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-slate-600" />
      </div>
    )
  }

  // Show loading while checking roles
  if (hasAccess === null) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-12 h-12 animate-spin text-slate-600" />
      </div>
    )
  }

  // Block access if user doesn't have required roles
  if (!hasAccess) {
    return (
      <div className="flex items-center justify-center h-screen bg-[#F2F3F7] p-4">
        <Card className="max-w-2xl p-6 border-red-200 bg-red-50">
          <div className="flex items-start gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 mt-0.5 flex-shrink-0" />
            <div className="flex-1">
              <h2 className="text-lg font-semibold text-red-900 mb-2">Access Denied</h2>
              <p className="text-red-800 mb-3">
                {unauthorizedMessage}
              </p>
            </div> 
          </div>
        </Card>
      </div>
    )
  }

  // User has access, render children
  return <>{children}</>
}

